<template>
  <div class="deal-list">
    <h2 class="mb-0">
      <icon-colored-exchange class="mr-2" />
      {{ capitalize($t('names.curatedDeal', { n: 2 })) }}
    </h2>
    <div class="row mb-4">
      <div
        id="curated_packages_list_table_container"
        class="col-12">
        <Table
          id="curated_packages_list_table"
          ref="table"
          category="Packages"
          first-column-size="md"
          pagination
          :items="curatedPackages"
          :allow-selection="selectionEnabled"
          :allow-selection-all="false"
          :lock-selection="lockSelection"
          :reset-selection-on-update="false"
          :can-select-item="isItemSelectable"
          :columns="columns"
          :show-filters="true"
          :total-count="props.curatedPackagesTotalCount"
          :is-loading="props.isCuratedPackagesLoading"
          :text-search-properties="['name', 'deal_id']"
          :is-main-table="false"
          :is-editable="false"
          :sort-column="curatedPackagesSortBy"
          :sort-ascending="isCuratedPackagesSortAscending"
          @filters-changed="tableFiltersChangedHandler"
          @page-size-changed="(e) => emits('pageSizeChanged', e)"
          @selection-changed="(e) => emits('selectionChanged', e)">
          <template #link="{ item: deal }">
            <button
              v-tooltip="
                capitalize($t(activeDeal === deal ? 'messages.closeDealDetailsPane' : 'messages.showTargetingDetails'))
              "
              data-testid="request-deal-details-button"
              class="btn btn-no-text btn-xxs btn-info-hollow mr-3"
              @click="emits('requestedDealDetails', deal)">
              <component
                :is="activeDeal === deal ? IconClose : IconEye"
                class="icon" />
            </button>
            <router-link
              :to="deal.endpoint"
              class="entity-link"
              style="margin-right: 0.6rem"
              :title="deal.name">
              {{ deal.name }}
            </router-link>
          </template>
          <template #dealID="{ item: deal }">{{ deal.dealID }}</template>
          <template #curator="{ item: deal }">
            {{ allCurators.find((curator) => curator.curatorOrganizationID === deal.organizationID)?.name }}
          </template>
          <template #co2EmissionsScore="{ item: deal }">
            <template v-if="deal.co2EmissionsScore > 0">
              <green-score
                style="width: 4.8rem; height: 2.4rem; font-size: 1.4rem"
                :is-animating="false"
                :show-green-leaf="false"
                :inline="true"
                :co2-score="deal.co2EmissionsScore" />
            </template>
            <template v-else>
              <span>{{ DASH }}</span>
            </template>
          </template>
          <template #purchaseType="{ item: deal }">
            <span>{{ deal.purchaseTypeName }}</span>
          </template>
          <template #dailyImpressionsFormatted="{ item: deal }">{{
            getValueOrDash(deal?.totalCuratedDeliveryStatistics?.dailyImpressionsFormatted)
          }}</template>
          <template #eCPM="{ item: deal }">{{ deal.totalCuratedCPMFormatted }}</template>
          <template #siteCountFormatted="{ item: deal }">{{
            getValueOrDash(deal?.totalCuratedDeliveryStatistics?.siteCountFormatted)
          }}</template>
          <template #unitCountFormatted="{ item: deal }">{{
            getValueOrDash(deal?.totalCuratedDeliveryStatistics?.unitCountFormatted)
          }}</template>
          <template #modifiedOn="{ item: deal }">{{ deal.modifiedOnFormatted }}</template>
          <template #no-records>
            <no-records-table
              :icon="markRaw(IconComplexDeal2)"
              :header-text="$t('messages.tablePlaceholderNoCuratedDealsTitleDemandSize')"
              :details-text="$t('messages.tablePlaceholderNoCuratedDealsDescriptionDemandSize')" />
          </template>
          <template #no-match>
            <no-records-table
              :icon="markRaw(IconComplexDeal2)"
              :header-text="$t('messages.tablePlaceholderNoMatchingDealsTitle')"
              :details-text="$t('messages.tablePlaceholderNoMatchingItemsDescription')" />
          </template>
        </Table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  //#region Imports

  import { capitalize, markRaw, ref, watch } from 'vue';

  import GreenScore from '@/common/components/controls/GreenScore.vue';
  import NoRecordsTable from '@/common/components/controls/table/NoRecordsTable.vue';
  import Table from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import { isItemSelectable } from '@/common/components/deals/DealsHelper';
  import { IconColoredExchange } from '@/common/icons/colored';
  import { IconComplexDeal2 } from '@/common/icons/complex';
  import { IconClose, IconEye } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import CuratorOrganization from '@/common/models/misc-api-models/CuratorOrganization';
  import { DASH, getValueOrDash } from '@/globals';

  //#endregion

  //#region Props

  interface ICuratedDealListProps {
    allCurators: CuratorOrganization[];
    curatedPackages: Package[];
    curatedPackagesTotalCount: number;
    curatedPackagesSortBy?: { label: string; property: string };
    isCuratedPackagesSortAscending?: boolean;
    isCuratedPackagesLoading: boolean;
    columns: Record<string, TableColumn>;
    pageSize: number;
    previousTableData?: typeof Table;
    selectionEnabled?: boolean;
    lockSelection?: boolean;
    activeDeal?: Package;
  }

  const props = withDefaults(defineProps<ICuratedDealListProps>(), {
    allCurators: () => [],
    curatedPackages: () => [],
    curatedPackagesTotalCount: 0,
    curatedPackagesSortBy: null,
    isCuratedPackagesSortAscending: true,
    isCuratedPackagesLoading: true,
    columns: null,
    pageSize: 10,
    previousTableData: null,
    selectionEnabled: true,
    lockSelection: false,
    activeDeal: null,
  });

  const emits = defineEmits([
    'filtersChanged',
    'pageSizeChanged',
    'loaded',
    'selectionChanged',
    'requestedDealDetails',
  ]);

  //#endregion

  //#region Data

  const table = ref(null);
  const restoredData = ref(props.previousTableData);

  //#endregion

  //#region Watches

  watch(
    () => table.value,
    () => {
      if (table.value) emits('loaded', table.value);
    },
  );

  watch(
    () => props.pageSize,
    () => {
      table.value?.setPageSize(props.pageSize);
    },
  );

  //#endregion

  //#region Methods

  const tableFiltersChangedHandler = (x) => {
    if (restoredData.value) {
      restoredData.value = null;
      return;
    }
    emits('filtersChanged', x);
  };

  //#endregion
</script>

<style lang="scss" scoped>
  @use '@/styles/variables.scss' as *;

  .green-score {
    width: 4.8rem;
    height: 2.4rem;
    font-size: 1.4rem;
  }
</style>
